import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, NgModule } from '@angular/core';
import { config, CognitoIdentityCredentials } from 'aws-sdk/global';
import { Observable, of, BehaviorSubject, throwError, from, EMPTY, Subject, forkJoin } from 'rxjs';
import { shareReplay, tap, filter, mergeMap, map, first, catchError, reduce, expand, delay, takeUntil, toArray } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as FoundryAuth from '@thinglogix/foundry-auth';
import AsyncLock from 'async-lock';
import { addDays, addYears } from 'date-fns';
import { BroadcastChannel } from 'broadcast-channel';
import { v1 } from 'uuid';
import axios from 'axios';
import { SHA256, enc, HmacSHA256 } from 'crypto-js';
import { inflate } from 'pako';

class BroadcastChannelService {
    constructor() {
        this.channels = new Map();
    }
    createChannel(name) {
        return new BroadcastChannel(name);
    }
    subscribe(channelName) {
        const channel = this.createOrGetChannel(channelName);
        return Observable.create((observer) => {
            channel.addEventListener('message', (message) => {
                observer.next(message);
            });
        });
    }
    unsubscribe(channelName) {
        if (this.channels.has(channelName)) {
            const channel = this.channels.get(channelName);
            channel.removeEventListener(channelName, () => {
                channel.close();
                this.channels.delete(channelName);
            });
        }
    }
    postMessage(channelName, message) {
        const channel = this.createOrGetChannel(channelName);
        channel.postMessage(message);
    }
    createOrGetChannel(channelName) {
        if (this.channels.has(channelName)) {
            return this.channels.get(channelName);
        }
        const channel = this.createChannel(channelName);
        this.channels.set(channelName, channel);
        return channel;
    }
}
BroadcastChannelService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BroadcastChannelService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BroadcastChannelService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BroadcastChannelService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BroadcastChannelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const FNDY_STORAGE_SERVICE = new InjectionToken('FNDY_STORAGE_SERVICE');

class BrowserIdService {
    constructor(storageService) {
        this.storageService = storageService;
    }
    async setBrowserId(expiration_days) {
        this.browserId = v1();
        const expireDate = addDays(new Date(), expiration_days).valueOf();
        await this.storageService.set('mfaExpireDays', String(expiration_days), {
            expires: expireDate,
            path: '/',
            domain: undefined,
            secure: false,
            sameSite: 'Lax',
        });
        await this.storageService.set('browserId', this.browserId, {
            expires: expireDate,
            path: '/',
            domain: undefined,
            secure: false,
            sameSite: 'Lax',
        });
    }
    async getBrowserId(expiration_days) {
        if (await this.storageService.check('browserId')) {
            this.browserId = await this.storageService.get('browserId');
            const mfaExpireDays = Number.parseInt(await this.storageService.get('mfaExpireDays'), 10);
            if (mfaExpireDays !== expiration_days || expiration_days === 0) {
                this.setBrowserId(expiration_days);
            }
        }
        else {
            this.setBrowserId(expiration_days);
        }
        return this.browserId;
    }
    async deleteCookies() {
        await this.storageService.remove('browserId');
        await this.storageService.remove('mfaExpireDays');
    }
    getBrowserName() {
        const magicTwo = 2;
        const ua = navigator.userAgent;
        let tem;
        let M = /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/.exec(ua) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return `IE ${tem[1] || ''}`;
        }
        if (M[1] === 'Chrome') {
            tem = /\bOPR\/(\d+)/.exec(ua);
            if (tem != null) {
                return `Opera ${tem[1]}`;
            }
        }
        M = M[magicTwo] ? [M[1], M[magicTwo]] : [navigator.appName, navigator.appVersion, '-?'];
        tem = /version\/(\d+)/.exec(ua);
        if (tem !== null) {
            M.splice(1, 1, tem[1]);
        }
        return `${M[0]} ${M[1]}`;
    }
    getOSName() {
        let OSName = 'Unknown OS';
        if (navigator.appVersion.indexOf('Win') !== -1) {
            OSName = 'Windows';
        }
        if (navigator.appVersion.indexOf('Mac') !== -1) {
            OSName = 'MacOS';
        }
        if (navigator.appVersion.indexOf('X11') !== -1) {
            OSName = 'UNIX';
        }
        if (navigator.appVersion.indexOf('Linux') !== -1) {
            OSName = 'Linux';
        }
        return OSName;
    }
}
BrowserIdService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BrowserIdService, deps: [{ token: FNDY_STORAGE_SERVICE }], target: i0.ɵɵFactoryTarget.Injectable });
BrowserIdService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BrowserIdService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: BrowserIdService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FNDY_STORAGE_SERVICE]
                }] }]; } });

class CacheService {
    constructor() {
        this.cache = new Map();
    }
    getValue(key, fallback$, putInWebStorage = false) {
        const prefixedKey = `fndy-cache-${key}`;
        if (!this.cache.has(prefixedKey)) {
            let cachedFallback$;
            const valueStr = localStorage.getItem(prefixedKey);
            if (putInWebStorage && valueStr) {
                cachedFallback$ = of(JSON.parse(valueStr));
            }
            else {
                cachedFallback$ = fallback$.pipe(shareReplay(1), tap((value) => {
                    if (putInWebStorage) {
                        try {
                            localStorage.setItem(prefixedKey, JSON.stringify(value));
                        }
                        catch {
                            console.error('Unable to put in local storage');
                        }
                    }
                }));
            }
            this.cache.set(prefixedKey, cachedFallback$);
        }
        return this.cache.get(prefixedKey);
    }
    invalidate(key) {
        const prefixedKey = key.startsWith('fndy-cache') ? key : `fndy-cache-${key}`;
        this.cache.delete(prefixedKey);
        localStorage.removeItem(prefixedKey);
    }
    invalidateRange(keyPrefix) {
        const prefixedKey = keyPrefix.startsWith('fndy-cache') ? keyPrefix : `fndy-cache-${keyPrefix}`;
        this.cache.forEach((_, key) => {
            if (key.startsWith(prefixedKey)) {
                this.invalidate(key);
            }
        });
    }
    clear() {
        this.invalidateRange('fndy-cache');
    }
}
CacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: CacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: CacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const FOUNDRY_API_CONFIG = new InjectionToken('FOUNDRY_API_CONFIG');
class FoundryApiModule {
    static forRoot(foundryApiConfig) {
        return {
            ngModule: FoundryApiModule,
            providers: [
                {
                    provide: FOUNDRY_API_CONFIG,
                    useValue: foundryApiConfig,
                },
            ],
        };
    }
}
FoundryApiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FoundryApiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FoundryApiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FoundryApiModule });
FoundryApiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FoundryApiModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FoundryApiModule, decorators: [{
            type: NgModule
        }] });

const cognitoAuth = new FoundryAuth.CognitoAuth();
class AuthService {
    constructor(httpClient, cacheService, broadcastService, foundryApiConfig, storageService, browserIdService) {
        this.httpClient = httpClient;
        this.cacheService = cacheService;
        this.broadcastService = broadcastService;
        this.foundryApiConfig = foundryApiConfig;
        this.storageService = storageService;
        this.browserIdService = browserIdService;
        this.passwordPolicies = [
            {
                name: 'Any characters',
                regex: '.',
                pattern: /./,
            },
            {
                name: 'One capital letter and one number',
                regex: '(?=.*\\d)(?=.*[A-Z])',
                pattern: /(?=.*\d)(?=.*[A-Z])/,
            },
            {
                name: 'One capital letter and one special character',
                regex: '(?=.*[$@$!%*?&])(?=.*[A-Z])',
                pattern: /(?=.*[$@$!%*?&])(?=.*[A-Z])/,
            },
            {
                name: 'One capital letter, one number and one special character',
                regex: '(?=.*[$@$!%*?&])(?=.*[A-Z])(?=.*\\d)',
                pattern: /(?=.*[$@$!%*?&])(?=.*[A-Z])(?=.*\d)/,
            },
            {
                name: 'Minimum 8 characters at least one capital letter and one number',
                regex: '((?=.*[A-Z])(?=.*\\d)).{8,}',
                pattern: /((?=.*[A-Z])(?=.*\d)).{8,}/,
            },
            {
                name: 'Minimum 8 characters at least one capital letter and one special character',
                regex: '(?=.*[$@$!%*?&])(?=.*[A-Z]).{8,}',
                pattern: /(?=.*[$@$!%*?&])(?=.*[A-Z]).{8,}/,
            },
            {
                name: 'Minimum 8 characters at least one capital letter, one number and one special character',
                regex: '(?=.*[$@$!%*?&])(?=.*[A-Z])(?=.*\\d).{8,}',
                pattern: /(?=.*[$@$!%*?&])(?=.*[A-Z])(?=.*\d).{8,}/,
            },
        ];
        this.authStateChange$ = new BehaviorSubject({
            state: null,
            user: null,
        });
        this._awsConfigRefreshed$ = new BehaviorSubject(null);
        this.awsConfigRefreshed$ = this._awsConfigRefreshed$.asObservable().pipe(filter((awsConfig) => awsConfig !== null));
        this._storedUsers$ = new BehaviorSubject([]);
        this.lock = new AsyncLock();
        this.storedUsers$ = this._storedUsers$.asObservable();
        this.watchAuthStateChange();
        if (this.user) {
            this.authStateChange$.next({
                state: 'signedIn',
                user: this.user,
            });
        }
    }
    get user() {
        return this._user ? this._user : JSON.parse(localStorage.getItem('user'));
    }
    set user(user) {
        this._user = user;
        if (user) {
            if (user.password) {
                delete user.password;
            }
            const userToSave = JSON.stringify(user);
            localStorage.setItem(`${user.id}_user`, userToSave);
            localStorage.setItem('user', userToSave);
        }
    }
    clearCognitoData() {
        this.clearCookies();
        this.cacheService.invalidateRange('cognitoData');
    }
    getCognitoData(account, domain) {
        const apiConfig = this.foundryApiConfig;
        if (apiConfig.cognitoData) {
            const { cognitoData } = apiConfig;
            return of({
                owner_id: cognitoData.foundryAccountId,
                api_gateway_endpoint: cognitoData.foundryApiGatewayEndpoint,
                cognito_identity_pool_id: cognitoData.foundryCognitoIdentityPoolId,
                cognito_developer_provider_name: cognitoData.foundryCognitoDeveloperProviderName,
            });
        }
        if (!apiConfig.rootApi) {
            return throwError('No api config provided!');
        }
        let params;
        if (domain) {
            params = { domain };
        }
        else {
            params = { account };
        }
        const options = {
            headers: new HttpHeaders({
                'x-api-key': apiConfig.rootApi.foundryApiKey,
            }),
            params,
        };
        const url = `${apiConfig.rootApi.foundryApiEndpoint}/foundry/cognito-data`;
        const key = `cognitoData#id#${account}`;
        return this.cacheService.getValue(key, this.httpClient.get(url, options));
    }
    refreshAWSConfig() {
        return new Observable((observer) => {
            void this.lock.acquire('key', async (done) => {
                const { user } = this;
                if (!user) {
                    done();
                    observer.next(false);
                    observer.complete();
                    return;
                }
                const cognitoConfig = {
                    apiGatewayEndpoint: `${user.cognito.api_gateway_endpoint}/foundry`,
                    cognitoDeveloperProviderName: user.cognito.cognito_developer_provider_name,
                    cognitoIdentityPoolId: user.cognito.cognito_identity_pool_id,
                };
                const uid = await this.storageService.get(`${this.user.id}_uid`);
                const key = await this.storageService.get(`${this.user.id}_key`);
                cognitoAuth
                    .refreshAWSConfig(uid, key, this.user.username, { config: cognitoConfig })
                    .then((configData) => {
                    config.region = configData.config.region;
                    config.credentials = configData.config.credentials;
                    if (configData.refreshed || !this._awsConfigRefreshed$.getValue()) {
                        this._awsConfigRefreshed$.next(configData.config);
                    }
                    observer.next(true);
                })
                    .catch((err) => {
                    observer.error(err);
                    observer.next(false);
                })
                    .finally(() => {
                    observer.complete();
                    done();
                });
            });
        });
    }
    clearAWSCredentials() {
        config.credentials = new CognitoIdentityCredentials({});
    }
    authorize(account, username, password, ignoreApiUserOnly = false, parentAccountId = undefined, recaptchaToken = undefined) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const cognitoConfig = {
                apiGatewayEndpoint: `${cognitoData.api_gateway_endpoint}/foundry`,
                cognitoDeveloperProviderName: cognitoData.cognito_developer_provider_name,
                cognitoIdentityPoolId: cognitoData.cognito_identity_pool_id,
            };
            return from(cognitoAuth.authorize(account, username, password, {
                config: cognitoConfig,
                parentAccountId,
                recaptchaToken,
            })).pipe(mergeMap(async (data) => {
                const { user, decryptKey, uid } = data;
                const awsConfig = data.config;
                config.credentials = awsConfig.credentials;
                localStorage.setItem('apiVersion', data.apiVersion);
                if (!user.active) {
                    throw new Error('User is not active');
                }
                if (user.api_user_only && !ignoreApiUserOnly) {
                    throw new Error('Authentication error. User can not access Foundry UI.');
                }
                user.cognito = cognitoData;
                if (!user.use_mfa) {
                    this.user = user;
                    this.authStateChange$.next({
                        state: 'signedIn',
                        user: this.user,
                    });
                }
                const date = new Date();
                await this.storageService.set(`${user.id}_uid`, uid, {
                    expires: addYears(date, 1),
                    path: '/',
                    domain: undefined,
                    secure: false,
                    sameSite: 'Lax',
                });
                await this.storageService.set(`${user.id}_key`, decryptKey, {
                    expires: addYears(date, 1),
                    path: '/',
                    domain: undefined,
                    secure: false,
                    sameSite: 'Lax',
                });
                this.tmpUser = user;
                return user;
            }));
        }));
    }
    getAccountMetadata(accountId) {
        const apiConfig = this.foundryApiConfig;
        if (!apiConfig.rootApi) {
            return throwError('No api config provided!');
        }
        const headers = {
            headers: new HttpHeaders({
                'x-api-key': apiConfig.rootApi.foundryApiKey,
            }),
        };
        return this.cacheService.getValue(`account-metadata#${accountId}`, this.httpClient
            .get(apiConfig.rootApi.foundryApiEndpoint.concat(`/foundry/account/${accountId}/metadata`), headers)
            .pipe(map((result) => {
            return result;
        })));
    }
    forgotPassword(email, account, recaptchaToken) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const params = {
                email,
                host: `${window.location.origin}/auth`,
                account_id: cognitoData.owner_id,
                root_account_id: cognitoData.owner_id,
                recaptchaToken,
            };
            return from(this.httpClient.post(cognitoData.api_gateway_endpoint.concat('/foundry/forgot-password'), params));
        }), first());
    }
    checkResetPassword(account, user_id, token) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            return from(this.httpClient.get(cognitoData.api_gateway_endpoint.concat(`/foundry/forgot-password?user_id=${user_id}&token=${token}`)));
        }), first());
    }
    loginWithSalesforce(code, url, account) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const body = {
                code,
                redirect_uri: `${window.location.origin}/auth/salesforce`,
                url,
            };
            return from(this.httpClient.post(cognitoData.api_gateway_endpoint.concat('/foundry/login/salesforce'), body));
        }), first());
    }
    activateUser(account_id, user_id) {
        return this.getCognitoData(account_id).pipe(mergeMap((cognitoData) => {
            return from(this.httpClient.put(cognitoData.api_gateway_endpoint.concat(`/foundry/user/${user_id}/activate`), {}));
        }), first());
    }
    setNewPassword(account, user_id, token, passwordMD5, password_type, recaptchaToken) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const params = {
                userId: user_id,
                token,
                passwordMD5,
                password_type,
                recaptchaToken,
            };
            return from(this.httpClient.put(cognitoData.api_gateway_endpoint.concat('/foundry/forgot-password'), params));
        }), first());
    }
    verifyBrowser(account, browser_id, user_id) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const params = {
                user_id,
                browser_id,
                browser_name: this.browserIdService.getBrowserName(),
                operating_system: this.browserIdService.getOSName(),
            };
            return from(this.httpClient.post(cognitoData.api_gateway_endpoint.concat('/foundry/user/verify'), params));
        }), mergeMap((data) => {
            if (data.success === 'Verified') {
                this.setUserDataForMFA();
                return of(null);
            }
            if (data.success === 'Email is sent') {
                this.signOutUser();
                return of(data);
            }
            return of(null);
        }), first());
    }
    verifyCode(account, browser_id, user_id, code) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            const params = {
                user_id,
                browser_id,
                code,
            };
            return from(this.httpClient.put(cognitoData.api_gateway_endpoint.concat('/foundry/user/verify'), params));
        }), tap((data) => {
            if (data.success === 'Verified') {
                this.setUserDataForMFA();
            }
        }), first());
    }
    signUp(account, params) {
        return this.getCognitoData(account).pipe(mergeMap((cognitoData) => {
            return from(this.httpClient.post(cognitoData.api_gateway_endpoint.concat('/foundry/signup2'), params));
        }), first());
    }
    signOutUser(suppressEvent) {
        if (!suppressEvent) {
            this.broadcastService.postMessage('logout', true);
        }
        const oldUser = this.user;
        this.user = null;
        this.cacheService.clear();
        this.clearCookies(oldUser);
        this.clearLocalStorage();
        this.authStateChange$.next({ state: 'signedOut', user: null });
    }
    switchUser(user) {
        this.user = user;
        this.authStateChange$.next({ state: 'switchUser', user });
    }
    clearLocalStorage() {
        // easier to clear everything and set the values back
        const storedUsers = this._storedUsers$.value;
        localStorage.clear();
        storedUsers.forEach((user) => {
            localStorage.setItem(`${user.id}_user`, JSON.stringify(user));
        });
    }
    getStoredUsers() {
        const users = Object.keys(localStorage)
            .filter((k) => k.includes('_user'))
            .map((k) => {
            return JSON.parse(localStorage.getItem(k));
        })
            .filter((user) => !this.user || user.id !== this.user.id);
        this._storedUsers$.next(users);
    }
    async clearCookies(user) {
        if (user) {
            await this.storageService.remove(`${user.id}_uid`);
            await this.storageService.remove(`${user.id}_key`);
        }
        else {
            await this.storageService.clear();
        }
    }
    setUserDataForMFA() {
        this.user = this.tmpUser;
        this.authStateChange$.next({ state: 'signedIn', user: this.user });
    }
    watchAuthStateChange() {
        this.authStateChange$.subscribe(({ state }) => {
            if (state === 'signedOut') {
                this.clearAWSCredentials();
            }
        });
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AuthService, deps: [{ token: i1.HttpClient }, { token: CacheService }, { token: BroadcastChannelService }, { token: FOUNDRY_API_CONFIG }, { token: FNDY_STORAGE_SERVICE }, { token: BrowserIdService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: CacheService }, { type: BroadcastChannelService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FOUNDRY_API_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FNDY_STORAGE_SERVICE]
                }] }, { type: BrowserIdService }]; } });

class SigV4Client {
    constructor() {
        this.sigV4Client = {};
        this.awsSigV4Client = {};
        this.AWS_SHA_256 = 'AWS4-HMAC-SHA256';
        this.AWS4_REQUEST = 'aws4_request';
        this.AWS4 = 'AWS4';
        this.X_AMZ_DATE = 'x-amz-date';
        this.X_AMZ_SECURITY_TOKEN = 'x-amz-security-token';
        this.HOST = 'host';
        this.AUTHORIZATION = 'Authorization';
    }
    newClient(config) {
        if (config.accessKey === undefined || config.secretKey === undefined) {
            return this.awsSigV4Client;
        }
        this.awsSigV4Client['accessKey'] = config.accessKey;
        this.awsSigV4Client['secretKey'] = config.secretKey;
        this.awsSigV4Client['sessionToken'] = config.sessionToken;
        this.awsSigV4Client['serviceName'] = config.serviceName || 'execute-api';
        this.awsSigV4Client['region'] = config.region || 'us-east-1';
        this.awsSigV4Client['defaultAcceptType'] = config.defaultAcceptType || 'application/json';
        this.awsSigV4Client['defaultContentType'] = config.defaultContentType || 'application/json';
        const invokeUrl = config.endpoint;
        const endpoint = /(^https?:\/\/[^/]+)/g.exec(invokeUrl)[1];
        const pathComponent = invokeUrl.substring(endpoint.length);
        this.awsSigV4Client['endpoint'] = endpoint;
        this.awsSigV4Client['pathComponent'] = pathComponent;
        this.awsSigV4Client['Get'] = (path, query, header) => {
            if (header === undefined) {
                header = {};
            }
            if (query === undefined) {
                query = {};
            }
            const getRequest = {
                method: 'get'.toUpperCase(),
                path,
                headers: header,
                queryParams: query,
                body: {},
            };
            return axios(this.signRequest(getRequest));
        };
        this.awsSigV4Client['Delete'] = (path, query, body, header) => {
            if (body === undefined) {
                body = { action: 'delete' };
            }
            if (query === undefined) {
                query = {};
            }
            if (header === undefined) {
                header = {};
            }
            const deleteRequest = {
                method: 'delete'.toUpperCase(),
                path,
                headers: header,
                queryParams: query,
                body,
            };
            return axios(this.signRequest(deleteRequest));
        };
        this.awsSigV4Client['Put'] = (path, query, body, header) => {
            if (body === undefined) {
                body = {};
            }
            if (query === undefined) {
                query = {};
            }
            if (header === undefined) {
                header = {};
            }
            const putRequest = {
                method: 'put'.toUpperCase(),
                path,
                headers: header,
                queryParams: query,
                body,
            };
            return axios(this.signRequest(putRequest));
        };
        this.awsSigV4Client['Post'] = (path, query, body, header) => {
            if (body === undefined) {
                body = {};
            }
            if (query === undefined) {
                query = {};
            }
            if (header === undefined) {
                header = {};
            }
            const postRequest = {
                method: 'post'.toUpperCase(),
                path,
                headers: header,
                queryParams: query,
                body,
            };
            return axios(this.signRequest(postRequest));
        };
        return this.awsSigV4Client;
    }
    hash(value) {
        return SHA256(value); // eslint-disable-line
    }
    hexEncode(value) {
        return value.toString(enc.Hex);
    }
    hmac(secret, value) {
        return HmacSHA256(value, secret, { asBytes: true }); // eslint-disable-line
    }
    buildCanonicalRequest(method, path, queryParams, headers, payload) {
        return `${method}\n${this.buildCanonicalUri(path)}\n${this.buildCanonicalQueryString(queryParams)}\n${this.buildCanonicalHeaders(headers)}\n${this.buildCanonicalSignedHeaders(headers)}\n${this.hexEncode(this.hash(payload))}`;
    }
    hashCanonicalRequest(request) {
        return this.hexEncode(this.hash(request));
    }
    buildCanonicalUri(uri) {
        return encodeURI(uri);
    }
    buildCanonicalQueryString(queryParams) {
        if (Object.keys(queryParams).length < 1) {
            return '';
        }
        const sortedQueryParams = [];
        for (const property in queryParams) {
            if (queryParams.hasOwnProperty(property)) {
                sortedQueryParams.push(property);
            }
        }
        sortedQueryParams.sort();
        let canonicalQueryString = '';
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < sortedQueryParams.length; i++) {
            canonicalQueryString += `${sortedQueryParams[i]}=${encodeURIComponent(queryParams[sortedQueryParams[i]])}&`;
        }
        return canonicalQueryString.substr(0, canonicalQueryString.length - 1);
    }
    buildCanonicalHeaders(headers) {
        let canonicalHeaders = '';
        const sortedKeys = [];
        for (const property in headers) {
            if (headers.hasOwnProperty(property)) {
                sortedKeys.push(property);
            }
        }
        sortedKeys.sort();
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < sortedKeys.length; i++) {
            canonicalHeaders += `${sortedKeys[i].toLowerCase()}:${headers[sortedKeys[i]]}\n`;
        }
        return canonicalHeaders;
    }
    buildCanonicalSignedHeaders(headers) {
        const sortedKeys = [];
        for (const property in headers) {
            if (headers.hasOwnProperty(property)) {
                sortedKeys.push(property.toLowerCase());
            }
        }
        sortedKeys.sort();
        return sortedKeys.join(';');
    }
    buildStringToSign(datetime, credentialScope, hashedCanonicalRequest) {
        return `${this.AWS_SHA_256}\n${datetime}\n${credentialScope}\n${hashedCanonicalRequest}`;
    }
    buildCredentialScope(datetime, region, service) {
        return (
        // eslint-disable-next-line no-magic-numbers
        `${datetime.substr(0, 8)}/${region}/${service}/${this.AWS4_REQUEST}`);
    }
    calculateSigningKey(secretKey, datetime, region, service) {
        return this.hmac(this.hmac(this.hmac(
        // eslint-disable-next-line no-magic-numbers
        this.hmac(this.AWS4 + secretKey, datetime.substr(0, 8)), region), service), this.AWS4_REQUEST);
    }
    calculateSignature(key, stringToSign) {
        return this.hexEncode(this.hmac(key, stringToSign));
    }
    extractHostname(url) {
        let hostname;
        if (url.indexOf('://') > -1) {
            // eslint-disable-next-line no-magic-numbers
            hostname = url.split('/')[2];
        }
        else {
            hostname = url.split('/')[0];
        }
        hostname = hostname.split(':')[0];
        hostname = hostname.split('?')[0];
        return hostname;
    }
    buildAuthorizationHeader(accessKey, credentialScope, headers, signature) {
        return `${this.AWS_SHA_256} Credential=${accessKey}/${credentialScope}, SignedHeaders=${this.buildCanonicalSignedHeaders(headers)}, Signature=${signature}`;
    }
    signRequest(request) {
        const verb = request.method.toUpperCase();
        const path = this.awsSigV4Client['pathComponent'] + request.path;
        const queryParams = { ...request.queryParams };
        const headers = { ...request.headers };
        // If the user has not specified an override for Content type the use default
        if (headers['Content-Type'] === undefined) {
            headers['Content-Type'] = this.awsSigV4Client['defaultContentType'];
        }
        // If the user has not specified an override for Accept type the use default
        if (headers['Accept'] === undefined) {
            headers['Accept'] = this.awsSigV4Client['defaultAcceptType'];
        }
        let body = { ...request.body };
        // override request body and set to empty when signing GET requests
        if (request.body === undefined || verb === 'GET') {
            body = '';
        }
        else {
            body = JSON.stringify(body);
        }
        // If there is no body remove the content-type header so it is not
        // included in SigV4 calculation
        if (body === '' || body === undefined || body === null) {
            delete headers['Content-Type'];
        }
        const datetime = new Date()
            .toISOString()
            .replace(/\.\d{3}Z$/, 'Z')
            .replace(/[:-]|\.\d{3}/g, '');
        headers[this.X_AMZ_DATE] = datetime;
        headers[this.HOST] = this.extractHostname(this.awsSigV4Client['endpoint']);
        const canonicalRequest = this.buildCanonicalRequest(verb, path, queryParams, headers, body);
        const hashedCanonicalRequest = this.hashCanonicalRequest(canonicalRequest);
        const credentialScope = this.buildCredentialScope(datetime, this.awsSigV4Client['region'], this.awsSigV4Client['serviceName']);
        const stringToSign = this.buildStringToSign(datetime, credentialScope, hashedCanonicalRequest);
        const signingKey = this.calculateSigningKey(this.awsSigV4Client['secretKey'], datetime, this.awsSigV4Client['region'], this.awsSigV4Client['serviceName']);
        const signature = this.calculateSignature(signingKey, stringToSign);
        headers[this.AUTHORIZATION] = this.buildAuthorizationHeader(this.awsSigV4Client['accessKey'], credentialScope, headers, signature);
        if (this.awsSigV4Client['sessionToken'] !== undefined && this.awsSigV4Client['sessionToken'] !== '') {
            headers[this.X_AMZ_SECURITY_TOKEN] = this.awsSigV4Client['sessionToken'];
        }
        delete headers[this.HOST];
        let url = this.awsSigV4Client['endpoint'] + path;
        const queryString = this.buildCanonicalQueryString(queryParams);
        if (queryString !== '') {
            url += `?${queryString}`;
        }
        // Need to re-attach Content-Type if it is not specified at this point
        if (headers['Content-Type'] === undefined) {
            headers['Content-Type'] = this.awsSigV4Client['defaultContentType'];
        }
        return {
            method: verb,
            headers,
            url,
            data: body,
        };
    }
}
SigV4Client.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SigV4Client, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SigV4Client.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SigV4Client, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SigV4Client, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ApiGatewayService {
    constructor(sigV4Client, authService, storageService) {
        this.sigV4Client = sigV4Client;
        this.authService = authService;
        this.storageService = storageService;
    }
    getApiClient(endpoint = `${this.authService.user.cognito.api_gateway_endpoint}/foundry`) {
        return this.authService.refreshAWSConfig().pipe(map(() => {
            const apiConfig = {
                accessKey: config.credentials.accessKeyId,
                secretKey: config.credentials.secretAccessKey,
                sessionToken: config.credentials.sessionToken,
                endpoint,
                region: config.region,
            };
            return this.sigV4Client.newClient(apiConfig);
        }), catchError(async (err) => {
            await this.storageService.remove(`${this.authService.user.id}_key`);
            console.error(err);
            return of(err);
        }));
    }
}
ApiGatewayService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ApiGatewayService, deps: [{ token: SigV4Client }, { token: AuthService }, { token: FNDY_STORAGE_SERVICE }], target: i0.ɵɵFactoryTarget.Injectable });
ApiGatewayService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ApiGatewayService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ApiGatewayService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SigV4Client }, { type: AuthService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FNDY_STORAGE_SERVICE]
                }] }]; } });

class FndyAccountService {
    constructor(apiGatewayService, cacheService) {
        this.apiGatewayService = apiGatewayService;
        this.cacheService = cacheService;
        this.tabMapping = {
            attributeView: 'DeviceAttributes',
            attributeList: 'DeviceData',
            history: 'DeviceHistory',
            parents: 'DeviceGroups',
            refList: 'References',
            postedData: 'DeviceMqttHistory',
        };
    }
    getSubAccounts(accountId, oneLevel, fields) {
        return this.cacheService.getValue(`accounts#${accountId}#${oneLevel}#${fields.toString()}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
                one_level: oneLevel,
                fields,
            };
            return from(apiClient.Get('/accounts', query));
        }), map((result) => result.data), 
        // Respose {format: string, zip: string} when zipped
        map((result) => result.format === 'gzip'
            ? JSON.parse(inflate(Buffer.from(result.zip, 'base64'), {
                to: 'string',
            }))
            : result), first()));
    }
    getAccount(accountId) {
        if (accountId === null) {
            return of(null);
        }
        return this.cacheService.getValue(`account#${accountId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/account/${accountId}`));
        }), map((result) => result.data), first()));
    }
    getAccountForceFully(accountId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/account/${accountId}`));
        }), map((result) => result.data), first());
    }
    getAccountAcl(accountId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const getAccountAclQuery = {
                account_id: accountId,
            };
            return from(apiClient.Get('/account/acl', getAccountAclQuery));
        }), map((result) => result.data), first());
    }
    createAccountAcl(acl) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = { inherit: acl.inherit };
            delete acl.inherit;
            const body = acl;
            return from(apiClient.Post('/account/acl', query, body));
        }), map((result) => result.data), first());
    }
    updateAccountAcl(acl) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = { inherit: acl.inherit };
            delete acl.inherit;
            const body = acl;
            return from(apiClient.Put('/account/acl', query, body));
        }), map((result) => result.data), first());
    }
    deleteAccountAcl(acl_id) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = { action: 'delete' };
            return from(apiClient.Delete(`/account/acl/${acl_id}`, {}, query));
        }), map((result) => result.data), first());
    }
    updateAccount(account) {
        this.cacheService.invalidateRange('accounts');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/account', {}, account));
        }), map((result) => result.data), first());
    }
    updateAccountPicture(file, accountId) {
        this.cacheService.invalidate(`account#${accountId}`);
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const payload = {
                data: file.data,
                type: file.type,
                account_id: accountId,
            };
            return from(apiClient.Post('/account/picture', {}, payload));
        }), map((result) => result.data), first());
    }
    createAccount(account) {
        this.cacheService.invalidateRange('accounts');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/account', {}, account));
        }), map((result) => result.data), first());
    }
    deleteAccount(accountId) {
        this.cacheService.invalidateRange('accounts');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/account/${accountId}`));
        }), map((result) => result.data), first());
    }
    getAccountPreferences(accountId) {
        return this.cacheService.getValue(`preferences#${accountId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/account/${accountId}/preferences`));
        }), map((result) => {
            return result.data;
        }), map((settings) => {
            settings.deviceTabs = this.mapLegacyTabsToNewTabs(settings.deviceTabs);
            return settings;
        }), first()), true);
    }
    updateAccountPreferences(accountId, settings) {
        this.cacheService.invalidateRange('preferences');
        const mergedTabs = this.mapNewTabsToLegacyTabs(settings.deviceTabs);
        settings.deviceTabs = mergedTabs;
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put(`/account/${accountId}/preferences`, {}, settings));
        }), map((result) => result.data), first());
    }
    updateAccountMetadata(accountId, metadata) {
        this.cacheService.invalidateRange('account-metadata');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put(`/account/${accountId}/metadata`, {}, metadata));
        }), map((result) => result.data), first());
    }
    checkAccountEmpty(accountId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
            };
            return from(apiClient.Get('/account-is-empty', query));
        }), map((result) => result.data), first());
    }
    mapLegacyTabsToNewTabs(objectTabs) {
        const mappedTabs = { ...objectTabs };
        for (const newTab of Object.keys(this.tabMapping)) {
            const legacyTab = this.tabMapping[newTab];
            if (legacyTab in objectTabs) {
                mappedTabs[newTab] = objectTabs[legacyTab];
            }
        }
        return mappedTabs;
    }
    mapNewTabsToLegacyTabs(objectTabs) {
        const mappedTabs = { ...objectTabs };
        for (const newTab of Object.keys(this.tabMapping)) {
            const legacyTab = this.tabMapping[newTab];
            if (newTab in objectTabs) {
                mappedTabs[legacyTab] = objectTabs[newTab];
            }
        }
        return mappedTabs;
    }
}
FndyAccountService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyAccountService, deps: [{ token: ApiGatewayService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyAccountService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyAccountService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyAccountService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }, { type: CacheService }]; } });

class AWSCertificateService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getCertificates() {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {};
            return from(apiClient.Get('/iot-certificate', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createCertificate(query) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/iot-certificate', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updateCertificate(certificateId, newStatus) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                certificateId,
                newStatus,
            };
            return from(apiClient.Put('/iot-certificate', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteCertificate(certificate) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                certificateArn: certificate.certificateArn,
                certificateId: certificate.certificateId,
            };
            return from(apiClient.Delete('/iot-certificate', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
AWSCertificateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AWSCertificateService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
AWSCertificateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AWSCertificateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: AWSCertificateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class FndyExportService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    callExport(body) {
        const params = body;
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Post('/export-config', {}, params))), map((result) => {
            return result.data;
        }), first());
    }
}
FndyExportService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyExportService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyExportService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyExportService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyExportService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class IAMCredentialsService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getIAMCredentials(accountId, defaultOnly) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                owner_id: accountId,
                default_only: defaultOnly,
            };
            return from(apiClient.Get('/iam-credentials', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createIAMCredentials(body) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/iam-credentials', {}, body));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updateIAMCredentials(body) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/iam-credentials', {}, body));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteIAMCredentials(credentialId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/iam-credentials/${credentialId}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
IAMCredentialsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: IAMCredentialsService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
IAMCredentialsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: IAMCredentialsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: IAMCredentialsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class FndyOAuthService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getTokens(accountId, platform) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                accountId,
                platform,
            };
            return from(apiClient.Get('/o-auth', query));
        }), map((result) => result.data), first());
    }
    // eslint-disable-next-line max-len
    // Here the parameter token is an array containing json key value pair, example [{account_id: a, token_key: b, token_value: c, platform:e}, {...}, {...}]
    postTokens(tokens) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const body = { tokens };
            return from(apiClient.Post('/o-auth', {}, body));
        }), map((result) => result.data), first());
    }
    deleteTokens(accountId, platform) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                accountId,
                platform,
            };
            return from(apiClient.Delete('/o-auth', query));
        }), map((result) => result.data), first());
    }
    // eslint-disable-next-line max-len
    // Here the parameter token is an array containing json key value pair, example [{account_id: a, token_key: b, token_value: c, platform:e}, {...}, {...}]
    putTokens(tokens) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const body = { tokens };
            return from(apiClient.Put('/o-auth', {}, body));
        }), map((result) => result.data), first());
    }
}
FndyOAuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyOAuthService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyOAuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyOAuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyOAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class ObjectSearchService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    search(searchBody) {
        return this.objectSearchApi('/object-search', searchBody);
    }
    exportSearch(searchBody) {
        return this.objectSearchApi('/object-search/export', searchBody);
    }
    objectSearchApi(path, searchBody) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post(path, {}, searchBody));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
ObjectSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ObjectSearchService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
ObjectSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ObjectSearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: ObjectSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

const CON_REQUEST_COUNT = 10;
const EXPORT_PAGE_INTERVAL = 1000;
const thinglogixRootGlobalActionTypeId = 'a5J60000000DnaLEAS';
class FndyObjectService {
    constructor(apiGatewayService, cacheService, httpClient) {
        this.apiGatewayService = apiGatewayService;
        this.cacheService = cacheService;
        this.httpClient = httpClient;
        /* eslint-disable @typescript-eslint/member-ordering */
        this._historyIsExporting$ = new BehaviorSubject(false);
        this.historyIsExporting$ = this._historyIsExporting$.asObservable();
    }
    getObjects(accountId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
            };
            return from(apiClient.Get('/devices', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectTypes(accountId, oneLevel) {
        let level = oneLevel;
        if (!level) {
            level = false;
        }
        return this.cacheService.getValue(`objectTypes#${accountId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
                one_level: level,
            };
            return from(apiClient.Get('/device-types', query));
        }), map((result) => {
            return result.data;
        }), first()));
    }
    getObjectTypeMap(accountId, oneLevel) {
        return this.getObjectTypes(accountId, oneLevel).pipe(mergeMap((objectTypes) => objectTypes), reduce((acc, objectType) => {
            acc.set(objectType.typeId, objectType);
            return acc;
        }, new Map()));
    }
    downloadObjectType(typeId, name, templates) {
        const params = { typeId, name, templates };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/device-type/download', {}, params));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteObjectType(typeId) {
        this.cacheService.invalidateRange('objectTypes');
        this.cacheService.invalidate(`objectType#${typeId}`);
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/device-type/${typeId}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObject(objectId, withReferences = false) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                withReferences,
            };
            return from(apiClient.Get(`/device/${encodeURIComponent(objectId)}`, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteObjectAttributes(objectId, attributesToDelete) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const deletePayload = {
                deviceId: objectId,
                deleted: attributesToDelete,
            };
            return from(apiClient.Put('/device-attributes', {}, deletePayload));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectsByType(typeId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                type_id: typeId,
            };
            return from(apiClient.Get('/devices/by-type', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getGlobalActions() {
        return this.cacheService.getValue('globalActions#', this.getObjectsByType(thinglogixRootGlobalActionTypeId));
    }
    getWorkflows() {
        return this.getObjectsByType(thinglogixRootGlobalActionTypeId).pipe(map((objectTypes) => {
            return objectTypes.filter((objectType) => objectType.isParent);
        }));
    }
    getAttributeGroups(accountId) {
        return this.cacheService.getValue(`attributeGroups#${accountId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const attributeQuery = {
                account_id: accountId,
            };
            return from(apiClient.Get('/group-attributes', attributeQuery));
        }), map((result) => {
            return result.data;
        }), first()));
    }
    createAttributeGroup(attributeGroup) {
        this.cacheService.invalidateRange('attributeGroups');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/group-attribute', {}, attributeGroup));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updateAttributeGroup(attributeGroup) {
        this.cacheService.invalidateRange('attributeGroups');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/group-attribute', {}, attributeGroup));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteAttributeGroup(groupId) {
        this.cacheService.invalidateRange('attributeGroups');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/group-attribute/${groupId}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteObject(objectId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/device/${encodeURIComponent(objectId)}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectHistory(objectId, limit, start, end, type) {
        return this.getObjectHistoryPage(objectId, limit, start, end, type).pipe(expand((objectHistory) => {
            if (objectHistory.length === limit) {
                return this.getObjectHistoryPage(encodeURIComponent(objectId), limit, objectHistory[objectHistory.length - 1].epochTime, end, type);
            }
            return EMPTY;
        }));
    }
    exportObjectHistory(data, type) {
        const unsubscribe$ = new Subject();
        let exportUrl;
        const successCode = 200;
        this._historyIsExporting$.next(true);
        this.export(data, type)
            .pipe(expand((url) => {
            return this.httpClient.get(url).pipe(mergeMap(() => of(url)), catchError((err) => {
                if (err.status === successCode) {
                    unsubscribe$.next();
                }
                return of(url);
            }), delay(EXPORT_PAGE_INTERVAL));
        }), takeUntil(unsubscribe$))
            .subscribe((url) => {
            exportUrl = url;
        }, () => { }, () => {
            this._historyIsExporting$.next(false);
            window.location.href = exportUrl;
        });
    }
    updateObject(fndyObject) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/device', {}, fndyObject));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updateObjectType(fndyObjectType) {
        this.cacheService.invalidateRange('objectTypes');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/device-type', {}, fndyObjectType));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createObjectType(fndyObjectType) {
        this.cacheService.invalidateRange('objectTypes');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/device-type', {}, fndyObjectType));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createObject(fndyObject) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/device', {}, fndyObject));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createObjects(fndyObjects) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/devices', {}, { devices: fndyObjects }));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectType(typeId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/device-type/${typeId}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getReferencesTo(objectId, with_type) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                with_types: with_type,
            };
            return from(apiClient.Get(`/reference/${encodeURIComponent(objectId)}/to`, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteReference(refTo, refFrom) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                refTo,
                refFrom,
            };
            return from(apiClient.Delete('/reference', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectsByName(accountId, searchText, groupsOnly) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
                groups_only: groupsOnly,
                search: searchText,
                search_field: 'a.name',
            };
            return from(apiClient.Get('/devices/search-service', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getParents(objectId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/device/${encodeURIComponent(objectId)}/parents`));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getChildren(objectId) {
        let client;
        const orderMap = {};
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            client = { ...apiClient };
            return from(apiClient.Get(`/device/${encodeURIComponent(objectId)}/children`));
        }), map((result) => result.data), mergeMap((children) => {
            return children;
        }), mergeMap((child) => {
            orderMap[child['deviceId']] = child['order'];
            if (child['typeId']) {
                return of(child);
            }
            return from(client.Get(`/device/${encodeURIComponent(child['deviceId'])}`)).pipe(catchError((error) => {
                console.error(error);
                return of(null);
            }));
        }, CON_REQUEST_COUNT), map((result) => {
            if (result && result.data) {
                return result.data;
            }
            if (result) {
                return result;
            }
            return null;
        }), toArray(), map((children) => {
            const filteredChildren = children.filter((child) => child != null);
            return filteredChildren.sort((child1, child2) => {
                return orderMap[child1.deviceId] - orderMap[child2.deviceId];
            });
        }));
    }
    createDeviceJunction(deviceId, parentId, order) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                parentId,
                deviceId,
                order,
            };
            return from(apiClient.Post('/device/junction', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteDeviceJunction(deviceId, parentId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const body = {
                parent_id: parentId,
                device_id: deviceId,
            };
            return from(apiClient.Delete('/device/junction', body));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getProvisioningRules(accountId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                userId: accountId,
            };
            return from(apiClient.Get('/iot-rules', query));
        }), map((result) => result.data), first());
    }
    createProvisioningRule(rule) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/iot-rules', {}, rule));
        }), map((result) => result.data), first());
    }
    updateProvisioningRule(rule) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/iot-rules/details', {}, rule));
        }), map((result) => result.data), first());
    }
    deleteProvisioningRule(accountId, ruleName) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const body = {
                userId: accountId,
                ruleName,
            };
            return from(apiClient.Delete('/iot-rules', {}, body));
        }), map((result) => result.data), first());
    }
    // Invoke Global Action
    invokeGlobalAction(deviceId, actionId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const body = {
                objectId: deviceId,
                actionId,
            };
            return from(apiClient.Post('/action', {}, body));
        }), map((result) => {
            return result.data;
        }), first());
    }
    invokeFoundryAction(actionType, actionEndpoint, payload) {
        const body = {
            payload,
        };
        if (actionType === 'lambda') {
            body.lambdaArn = actionEndpoint;
        }
        if (actionType === 'httpPost') {
            body.httpUrl = actionEndpoint;
        }
        if (actionType === 'mqttTopic') {
            body.mqtttTopic = actionEndpoint;
        }
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/action/no-device-id', {}, body));
        }), map((result) => {
            return result.data;
        }), first());
    }
    saveScheduledGADevices(account_id, devices) {
        const params = {
            account_id,
            devices,
        };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/devices/scheduled', {}, params));
        }), map((result) => {
            return result.data;
        }), first());
    }
    export(data, type) {
        const URL = type === 'history' ? '/device/history/export' : '/device/history/mqtt/export';
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post(URL, {}, data));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectHistoryPage(objectId, limit, start, end, historyType) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                device_id: encodeURIComponent(objectId),
                limit,
                start,
                end,
            };
            return from(apiClient.Get(historyType === 'history' ? '/device/history' : '/device/history/mqtt', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
FndyObjectService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyObjectService, deps: [{ token: ApiGatewayService }, { token: CacheService }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
FndyObjectService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyObjectService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyObjectService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }, { type: CacheService }, { type: i1.HttpClient }]; } });

class FndyPageMenu {
}

class FndyPageService {
    constructor(apiGatewayService, cacheService) {
        this.apiGatewayService = apiGatewayService;
        this.cacheService = cacheService;
    }
    getPortalPages(accountId) {
        return this.cacheService.getValue(`portalPages#${accountId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
            };
            return from(apiClient.Get('/portal-pages', query));
        }), map((result) => {
            return result.data;
        }), first()), true);
    }
    createPortalPage(page) {
        this.cacheService.invalidateRange('portalPages');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/portal-page', {}, page));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updatePortalPage(page) {
        this.cacheService.invalidateRange('portalPages');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/portal-page', {}, page));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deletePortalPage(pageId) {
        this.cacheService.invalidateRange('portalPages');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/portal-page/${pageId}`));
        }), map((result) => result.data), first());
    }
}
FndyPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyPageService, deps: [{ token: ApiGatewayService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyPageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }, { type: CacheService }]; } });

class FndyQuickSightService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getEmbedUrl(dashboardId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            if (dashboardId) {
                return from(apiClient.Get(`/quicksight/embed-url/${dashboardId}`, {}, {}));
            }
            return from(apiClient.Get(`/quicksight/embed-url`, {}, {}));
        }), map((result) => result.data), first());
    }
}
FndyQuickSightService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyQuickSightService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyQuickSightService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyQuickSightService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyQuickSightService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class S3Service {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    uploadFile(fileToUpload) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const params = {
                file: fileToUpload.data,
                name: fileToUpload.name,
                type: fileToUpload.type,
                size: fileToUpload.size,
                s3AccessLevel: fileToUpload.s3AccessLevel,
            };
            return from(apiClient.Post('/s3', {}, params));
        }), map((result) => result.data), first());
    }
    getBucketList() {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {};
            return from(apiClient.Get('/s3/bucket', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    getObjectsList(bucketName) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                bucket: bucketName,
            };
            return from(apiClient.Get('/s3', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    createBucket(bucketName) {
        const query = {
            bucket: bucketName,
        };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/s3/bucket', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    putObject(object) {
        const query = {
            bucket: object.bucket,
            file: object.file,
            name: object.name,
            originalName: true,
            size: object.Size,
        };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/s3', {}, query));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteObject(object) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                bucket: object.bucket,
                key: object.Key,
            };
            return from(apiClient.Delete('/s3', query));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
S3Service.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: S3Service, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
S3Service.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: S3Service, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: S3Service, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

var SearchType;
(function (SearchType) {
    SearchType["AdvancedSearch"] = "AdvancedSearch";
    SearchType["ObjectSearch"] = "ObjectSearch";
})(SearchType || (SearchType = {}));

class SavedSearchService {
    constructor(apiGatewayService, cacheService) {
        this.apiGatewayService = apiGatewayService;
        this.cacheService = cacheService;
    }
    getSavedSearches(id) {
        return this.cacheService.getValue(`savedSearches#${id}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const params = {
                userId: id,
            };
            return from(apiClient.Get('/saved-search', params));
        }), map((result) => {
            return result.data;
        }), first()));
    }
    getAllSavedSearches(userId, accountId) {
        const userSavedSearches$ = this.getSavedSearches(userId);
        const accountSavedSearches$ = this.getSavedSearches(accountId);
        return forkJoin([userSavedSearches$, accountSavedSearches$]).pipe(map(([userSavedSearches, accountSavedSearches]) => {
            return [...userSavedSearches, ...accountSavedSearches];
        }));
    }
    getSavedSearch(searchId, userId) {
        return this.cacheService.getValue(`savedSearch#${userId}#${searchId}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const params = {
                userId,
            };
            return from(apiClient.Get(`/saved-search/${searchId}`, params));
        }), map((result) => {
            return result.data;
        }), first()));
    }
    createSavedSearch(savedSearch) {
        this.cacheService.invalidateRange('savedSearch');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/saved-search', {}, savedSearch));
        }), map((result) => {
            return result.data;
        }), first());
    }
    updateSavedSearch(savedSearch) {
        this.cacheService.invalidateRange('savedSearch');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/saved-search', {}, savedSearch));
        }), map((result) => {
            return result.data;
        }), first());
    }
    deleteSavedSearch(searchId) {
        this.cacheService.invalidateRange('savedSearch');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/saved-search/${searchId}`));
        }), map((result) => {
            return result.data;
        }), first());
    }
}
SavedSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SavedSearchService, deps: [{ token: ApiGatewayService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable });
SavedSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SavedSearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: SavedSearchService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }, { type: CacheService }]; } });

class FndySfdcService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getUserDetail(accessToken, identity_url) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                access_token: accessToken,
                identityUrl: identity_url,
            };
            return from(apiClient.Post('/sfdc/user-details', {}, query));
        }), map((result) => result.data), first());
    }
    getToken(code, service_type) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                code,
                service_type,
            };
            return from(apiClient.Get('/sfdc/tokens', query));
        }), map((result) => result.data), first());
    }
    getIdentityUrl(refresh_token, service_type) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                refresh_token,
                service_type,
            };
            return from(apiClient.Get('/sfdc/identity-url', query));
        }), map((result) => result.data), first());
    }
}
FndySfdcService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndySfdcService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
FndySfdcService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndySfdcService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndySfdcService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class FndyUserSecretService {
    constructor(apiGatewayService) {
        this.apiGatewayService = apiGatewayService;
    }
    getSecrets(userId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Get('/user/secrets', { userId }))), map((result) => result.data), first());
    }
    createSecret(userId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Post('/user/secret', {}, { user_id: userId }))), map((result) => result.data), first());
    }
    deleteSecret(secret) {
        const query = {
            apiKeyId: secret.api_key_id,
            userId: secret.user_id,
        };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Delete('/user/secret', query))), map((result) => result.data), first());
    }
    updateSecretStatus(secret) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Put('/user/secret', {}, secret))), map((result) => result.data), first());
    }
}
FndyUserSecretService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserSecretService, deps: [{ token: ApiGatewayService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyUserSecretService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserSecretService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserSecretService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }]; } });

class FndyUserService {
    constructor(apiGatewayService, cacheService) {
        this.apiGatewayService = apiGatewayService;
        this.cacheService = cacheService;
    }
    getUsers(accountId, oneLevel = true) {
        return this.cacheService.getValue(`users#${accountId}#${oneLevel.toString()}`, this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            const query = {
                account_id: accountId,
                one_level: oneLevel,
            };
            return from(apiClient.Get('/users', query));
        }), map((result) => result.data), first()));
    }
    exportUsers() {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/users/export', {}, {}));
        }), map((result) => result.data), first());
    }
    getUser(userId, withObjects = false) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Get(`/user/${userId}`, { withObjects }));
        }), map((result) => result.data), first());
    }
    updateUser(user) {
        this.cacheService.invalidateRange('users');
        if (user.full_name) {
            delete user.full_name;
        }
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Put('/user', {}, user));
        }), map((result) => result.data), first());
    }
    createUser(user) {
        this.cacheService.invalidateRange('users');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            user.host = `${window.location.origin}/auth`;
            return from(apiClient.Post('/user', {}, user));
        }), map((result) => result.data), first());
    }
    deleteUser(userId) {
        this.cacheService.invalidateRange('users');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Delete(`/user/${userId}`));
        }), map((result) => result.data), first());
    }
    resendActivationEmail(username, rootAccount) {
        const params = {
            username,
            host: `${window.location.origin}/auth`,
            root_account_id: rootAccount,
        };
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => {
            return from(apiClient.Post('/user/resend-activation', {}, params));
        }), map((result) => result.data), first());
    }
    getSecrets(userId) {
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Get('/user/secrets', { user_id: userId }))), map((result) => result.data), first());
    }
    createSecret(userId) {
        // this.cacheService.invalidateRange('users');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Post('/user/secret', {}, { user_id: userId }))), map((result) => result.data), first());
    }
    deleteSecret(apiKeyId) {
        // this.cacheService.invalidateRange('users');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Delete('/user/secret ', { api_key_id: apiKeyId }))), map((result) => result.data), first());
    }
    updateSecret(secret) {
        // this.cacheService.invalidateRange('users');
        return this.apiGatewayService.getApiClient().pipe(mergeMap((apiClient) => from(apiClient.Put('/user/secret', {}, secret))), map((result) => result.data), first());
    }
}
FndyUserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserService, deps: [{ token: ApiGatewayService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable });
FndyUserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.5", ngImport: i0, type: FndyUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiGatewayService }, { type: CacheService }]; } });

// core services

/**
 * Generated bundle index. Do not edit.
 */

export { AWSCertificateService, ApiGatewayService, AuthService, BroadcastChannelService, BrowserIdService, CacheService, FNDY_STORAGE_SERVICE, FOUNDRY_API_CONFIG, FndyAccountService, FndyExportService, FndyOAuthService, FndyObjectService, FndyPageMenu, FndyPageService, FndyQuickSightService, FndySfdcService, FndyUserSecretService, FndyUserService, FoundryApiModule, IAMCredentialsService, ObjectSearchService, S3Service, SavedSearchService, SearchType, SigV4Client };
